import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { DataService } from 'src/app/data.service';

/**
 *  LAPTOP-KÖLCSÖNZÉS – ki vitt el egy gépet, mikorra hozza vissza.
 *
 *  Egyszerű kölcsönzés-napló: sorozatszám, átvevő (dolgozó vagy tanuló), kiadás
 *  napja, vállalt visszahozatal, visszavétel jelölése. A KÉSÉST a kiszolgáló
 *  számolja, nem ez a képernyő: a böngésző órája állítható, és a „hány napja
 *  késik" nem lehet nézetenként más.
 *
 *  Az átvevő nevét a kiadáskor lementjük a sorba. A kölcsönzés évekig megmarad,
 *  a tanuló viszont kiiratkozik, a kolléga kilép – az azonosító mellől eltűnne a
 *  név, és egy régi sor semmit nem érne, ha épp azt kellene kideríteni, kinél
 *  maradt a gép.
 */
@Component({
  selector: 'app-laptop',
  templateUrl: './laptop.component.html',
  styleUrls: ['./laptop.component.scss'],
})
export class LaptopComponent implements OnInit {
  private readonly alap = '/api/laptop';

  intezmeny = '';
  tetelek: any[] = [];
  osszesito: any = { osszes: 0, kint: 0, kesik: 0 };
  toltodik = true;
  fut = false;

  szures: 'mind' | 'kint' | 'kesik' | 'visszahozva' = 'kint';
  kereses = '';

  /* ── kiadás / szerkesztés ─────────────────────────────────────────── */
  modalNyitva = false;
  szerkesztettId: string | null = null;
  urlap: any = this.uresUrlap();

  szemelyek: any[] = [];
  szemelySzuro = '';
  szemelyTipus: 'mind' | 'dolgozo' | 'tanulo' = 'mind';

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    const sajat = await this.data.getFromStorage('sajatIntezmeny');
    this.intezmeny = sajat?.intezmenyId || '';
    await this.betolt();
    this.szemelyekBetolt();
  }

  private get ut() { return encodeURI(this.intezmeny); }

  private uresUrlap() {
    return {
      sorozatszam: '',
      atvevo: '',
      atvevo_nev: '',
      atvevo_tipus: 'dolgozo',
      kiadva: new Date().toISOString().slice(0, 10),
      varhato_vissza: '',
      megjegyzes: '',
    };
  }

  async betolt() {
    this.toltodik = true;
    try {
      const p = new URLSearchParams({ allapot: this.szures });
      if (this.kereses.trim()) p.set('q', this.kereses.trim());
      const r: any = await firstValueFrom(
        this.http.get(`${this.alap}/lista/${this.ut}?${p.toString()}`));
      this.tetelek = r?.tetelek || [];
      this.osszesito = r?.osszesito || { osszes: 0, kint: 0, kesik: 0 };
    } catch (e: any) {
      this.tetelek = [];
      this.data.sendToast(e?.error?.error || 'A lista nem tölthető be.', 'error');
    } finally {
      this.toltodik = false;
    }
  }

  async szemelyekBetolt() {
    try {
      this.szemelyek = await firstValueFrom(
        this.http.get<any[]>(`${this.alap}/szemelyek/${this.ut}`)) || [];
    } catch (e) { this.szemelyek = []; }
  }

  /** A választó kereshető és névsorban áll – több száz fős listában ez az egyetlen használható út. */
  get szurtSzemelyek(): any[] {
    const k = this.szemelySzuro.trim().toLowerCase();
    return (this.szemelyek || [])
      .filter((sz) => this.szemelyTipus === 'mind' || sz.tipus === this.szemelyTipus)
      .filter((sz) => !k
        || String(sz.nev || '').toLowerCase().includes(k)
        || String(sz.azonosito || '').toLowerCase().includes(k))
      .slice(0, 200);
  }

  szuresValt(ertek: any) {
    this.szures = ertek;
    this.betolt();
  }

  /* ── modál ────────────────────────────────────────────────────────── */

  ujKiadas() {
    this.szerkesztettId = null;
    this.urlap = this.uresUrlap();
    this.szemelySzuro = '';
    this.modalNyitva = true;
  }

  szerkeszt(t: any) {
    this.szerkesztettId = t.id;
    this.urlap = {
      sorozatszam: t.sorozatszam || '',
      atvevo: t.atvevo || '',
      atvevo_nev: t.atvevo_nev || '',
      atvevo_tipus: t.atvevo_tipus || 'dolgozo',
      kiadva: t.kiadva || '',
      varhato_vissza: t.varhato_vissza || '',
      megjegyzes: t.megjegyzes || '',
    };
    this.szemelySzuro = '';
    this.modalNyitva = true;
  }

  modalZar() { this.modalNyitva = false; }

  szemelyValaszt(sz: any) {
    this.urlap.atvevo = sz.azonosito;
    this.urlap.atvevo_nev = sz.nev;
    this.urlap.atvevo_tipus = sz.tipus;
  }

  get menthet(): boolean {
    return !!String(this.urlap.sorozatszam || '').trim()
      && !!String(this.urlap.atvevo || '').trim()
      && !!this.urlap.kiadva;
  }

  async ment() {
    if (!this.menthet || this.fut) return;
    this.fut = true;
    try {
      if (this.szerkesztettId) {
        await firstValueFrom(this.http.put(`${this.alap}/${this.szerkesztettId}`, this.urlap));
        this.data.sendToast('Módosítva.', 'success');
      } else {
        await firstValueFrom(this.http.post(this.alap, { ...this.urlap, intezmeny: this.intezmeny }));
        this.data.sendToast('Kiadás rögzítve.', 'success');
      }
      this.modalNyitva = false;
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A mentés nem sikerült.', 'error');
    } finally {
      this.fut = false;
    }
  }

  /* ── visszavétel ──────────────────────────────────────────────────── */

  async vissza(t: any) {
    const mikor = prompt('A visszavétel napja (ÉÉÉÉ-HH-NN):',
      new Date().toISOString().slice(0, 10));
    if (mikor === null) return;
    try {
      await firstValueFrom(this.http.post(`${this.alap}/${t.id}/vissza`, { visszahozva: mikor }));
      this.data.sendToast('Visszavéve.', 'success');
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A visszavétel nem sikerült.', 'error');
    }
  }

  /** Elgépelt gomb után ne kelljen új sort felvenni. */
  async visszaMegse(t: any) {
    if (!confirm('Visszavonod a visszavételt? A gép újra „kint" állapotba kerül.')) return;
    try {
      await firstValueFrom(this.http.post(`${this.alap}/${t.id}/megse-vissza`, {}));
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A visszavonás nem sikerült.', 'error');
    }
  }

  async torol(t: any) {
    if (!confirm(`Törlöd a bejegyzést?\n\n${t.sorozatszam} — ${t.atvevo_nev || t.atvevo}\n\n`
      + 'A kölcsönzés nyoma is eltűnik. Ha a gép visszaérkezett, inkább vedd vissza.')) return;
    try {
      await firstValueFrom(this.http.delete(`${this.alap}/${t.id}`));
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A törlés nem sikerült.', 'error');
    }
  }

  /* ── megjelenítés ─────────────────────────────────────────────────── */

  allapotSzoveg(t: any): string {
    if (t.visszahozva) return 'visszahozva';
    if (t.kesik) return `késik ${t.keses_nap} napja`;
    return 'kint';
  }

  allapotSzin(t: any): string {
    if (t.visszahozva) return 'success';
    return t.kesik ? 'danger' : 'medium';
  }

  tipusNev(t: string): string {
    return t === 'tanulo' ? 'tanuló' : 'dolgozó';
  }
}
